import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';

interface Project {
  id: number;
  name: string;
  subtitle: string;
  icon_url: string;
  amount: number;
  claimed: boolean;
  claim_status: string | null;
}

const numberLabels = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10'];

function formatWan(amount: number) {
  if (amount >= 10000) return `${Math.round(amount / 10000)}万`;
  return `${amount}元`;
}

export default function AssetRestore() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalProjects, setTotalProjects] = useState(0);
  const [claimedCount, setClaimedCount] = useState(0);
  const [claiming, setClaiming] = useState<number | null>(null);
  const [claimingAll, setClaimingAll] = useState(false);

  function loadProjects() {
    api.get('/asset-restore/projects').then(({ data }) => {
      setProjects(data.projects || []);
      setTotalAmount(data.total_amount || 0);
      setTotalProjects(data.total_projects || 0);
      setClaimedCount(data.claimed_count || 0);
    }).catch(() => {});
  }

  useEffect(() => { loadProjects(); }, []);

  async function handleClaim(projectId: number) {
    if (claiming !== null) return;
    setClaiming(projectId);
    try {
      await api.post('/asset-restore/claim', { project_id: projectId });
      toast.success('领取成功，等待审核');
      loadProjects();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '领取失败');
    } finally {
      setClaiming(null);
    }
  }

  async function handleClaimAll() {
    if (claimingAll) return;
    setClaimingAll(true);
    try {
      const { data } = await api.post('/asset-restore/claim-all');
      toast.success(data.message || '领取成功');
      loadProjects();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '领取失败');
    } finally {
      setClaimingAll(false);
    }
  }

  const allClaimed = projects.length > 0 && projects.every(p => p.claimed);

  if (allClaimed) {
    return (
      <div className="max-w-md mx-auto min-h-screen relative">
        <AuthGuard />
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/20 text-white"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
        <img src="/images/assets-success.png" alt="资产已完全恢复" className="w-full" />
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto min-h-screen pb-8 bg-[url(/images/huifu_beijing.png)] bg-[length:100%_100%] bg-no-repeat pt-[320px] relative" style={{ backgroundColor: '#8b0000' }}>
      <AuthGuard />

      {/* Hero background */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-4 left-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/20 text-white"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
      </button>

      {/* Content */}
      <div className="px-4 -mt-4 relative z-10">
        {/* Project list */}
        <div className="space-y-0">
          {projects.map((project, idx) => (
            <div
              key={project.id}
              className="bg-[url(/images/huifu_kuang.png)] bg-cover bg-top bg-no-repeat rounded-lg border-b border-[#ebbc9d] px-3 py-1 flex items-center gap-3"
            >
              <div className="flex flex-1">
                {/* Number badge */}
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#f5d998] to-[#d4a64e] flex items-center justify-center flex-shrink-0">
                  <span className="text-[#fff] text-xs font-bold">{numberLabels[idx] || String(idx + 1).padStart(2, '0')}</span>
                </div>

                {/* Icon */}
                <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-white/5">
                  <img src={project.icon_url} alt="" className="w-full h-full object-contain" />
                </div>

                {/* Info */}
                <div className="flex flex-1 justify-between gap-1 min-w-0">
                  <div className="flex flex-col gap-0.5">
                    <div className="text-[#960205] text-sm font-bold truncate">{project.name}</div>
                    <div className="text-[#646061] text-[10px] truncate">{project.subtitle}</div>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[#646061] text-[10px]">恢复资产</span><span className="text-[#c61217] text-md font-bold">{formatWan(project.amount)}</span>
                  </div>
                </div>
              </div>
              {/* Action button */}
              <div className="flex w-[80px]">
                {project.claimed ? (
                  <div className="relative opacity-60">
                    <img src={"/images/huifu_huiufi.png"} alt="恢复资产" className="w-[80px] h-full object-cover" />
                    <span className="absolute inset-0 flex items-center justify-center text-[#fbeae4] text-sm">{project.claim_status === 'approved' ? '已通过' : project.claim_status === 'rejected' ? '已拒绝' : '审核中'}</span>
                  </div>
                ) : (
                  <button
                    onClick={() => handleClaim(project.id)}
                    disabled={claiming === project.id}
                    className={`relative ${claiming === project.id ? 'opacity-60' : ''}`}
                  >
                    <img src={"/images/huifu_huiufi.png"} alt="恢复资产" className="w-[80px] h-full object-cover" />
                    <span className="absolute inset-0 flex items-center justify-center text-[#fbeae4] text-sm">恢复资产</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom claim all button */}
        {projects.length > 0 && (
          <div className="mt-3">
            <button
              onClick={handleClaimAll}
              disabled={claimingAll}
              className="max-w-[330px] mx-auto block relative"
            >
              <img
                src="/images/huifu_yijian.png"
                alt="一键领取全部资产"
                className={`w-full ${claimingAll ? 'opacity-60' : ''}`}
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
